import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Search, Download, Upload, Lock, History, ArrowLeft, Shield, Eye, Edit2, Trash2, X, ChevronUp, ChevronDown, Calendar, FileText, AlertTriangle, CheckCircle, Clock, Package, Wrench } from 'lucide-react';

const API_URL = 'https://calibration-management.v-kit.com/api';

const CalibrationTracker = () => {
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [certificates, setCertificates] = useState([]);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [equipmentData, setEquipmentData] = useState({});
  const [equipmentSchemas, setEquipmentSchemas] = useState({});
  const [customers, setCustomers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [wipData, setWipData] = useState({ data: [], grouped: {} });
  const [viewMode, setViewMode] = useState('wip');
  const [selectedSerial, setSelectedSerial] = useState(null);
  const [selectedEquipmentType, setSelectedEquipmentType] = useState('LFM');
  const [searchTerm, setSearchTerm] = useState('');
  const [tableFilter, setTableFilter] = useState('');
  const [loading, setLoading] = useState(false);

  // Modals
  const [showAddCertificate, setShowAddCertificate] = useState(false);
  const [showAddEquipment, setShowAddEquipment] = useState(false);
  const [showAddEquipmentStep2, setShowAddEquipmentStep2] = useState(false);
  const [showBookInModal, setShowBookInModal] = useState(false);
  const [showEditEquipment, setShowEditEquipment] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [duplicateCheck, setDuplicateCheck] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [historyData, setHistoryData] = useState(null);

  // Forms
  const [newCertificate, setNewCertificate] = useState({
    tool: '', serial_number: '', cal_date: '', exp_months: '12', result: 'PASS'
  });
  const [newEquipmentStep1, setNewEquipmentStep1] = useState({
    type: 'LFM',
    serial_number: '',
    printer_sn: ''
  });
  const [newEquipment, setNewEquipment] = useState({});
  const [bookInData, setBookInData] = useState({
    date_received: '',
    recal: 'Y',
    customer_name: '',
    sop: '',
    comments: ''
  });

  const [sortConfig, setSortConfig] = useState({ key: 'cal_date', direction: 'desc' });

  const equipmentConfigs = {
    LFM: { name: 'Liquid Flow Meters', color: 'blue', serialField: 'serial_number' },
    LFM2: { name: 'Liquid Flow Meters 2', color: 'cyan', serialField: 'serial_number' },
    PRT: { name: 'Printers', color: 'green', serialField: 'printer_sn' },
    GFM: { name: 'Gas Flow Meters', color: 'yellow', serialField: 'serial_number' },
    DTM: { name: 'Digital Thermometers', color: 'orange', serialField: 'serial_number' },
    DMA: { name: 'Digital Manometers', color: 'red', serialField: 'serial_number' }
  };

  const statusColors = {
    pre_booked: 'bg-gray-100 text-gray-700',
    arrived: 'bg-blue-100 text-blue-700',
    in_calibration: 'bg-yellow-100 text-yellow-700',
    calibrated: 'bg-green-100 text-green-700',
    ready_dispatch: 'bg-green-200 text-green-800',
    dispatched: 'bg-gray-200 text-gray-600'
  };

  useEffect(() => {
    const savedUser = localStorage.getItem('calibration_user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
      loadData();
    }
  }, []);

  const api = async (endpoint, method = 'GET', data = null) => {
    const token = user?.token || localStorage.getItem('calibration_user_token');
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token || ''
      }
    };
    if (data) options.body = JSON.stringify(data);

    const response = await fetch(`${API_URL}${endpoint}`, options);
    const result = await response.json();

    if (!response.ok) {
      if (response.status === 401) handleLogout();
      throw new Error(result.message || 'API Error');
    }
    return result;
  };

  const handleLogin = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/login.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      const result = await res.json();

      if (result.success) {
        const userData = { ...result.user, token: result.token };
        setUser(userData);
        localStorage.setItem('calibration_user', JSON.stringify(userData));
        localStorage.setItem('calibration_user_token', result.token);
        loadData();
      } else {
        alert(result.message || 'Login failed');
      }
    } catch (err) {
      alert('Login error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.clear();
    setCertificates([]);
    setEquipmentData({});
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [certs, types, custs, stats, wip] = await Promise.all([
        api('/certificates/list.php'),
        api('/equipment/types.php'),
        api('/customers/list.php'),
        api('/statuses/list.php'),
        api('/equipment/work-in-progress.php')
      ]);

      setCertificates(certs.data || []);
      setEquipmentTypes(types.data || []);
      setCustomers(custs.data || []);
      setStatuses(stats.data || []);
      setWipData({ data: wip.data || [], grouped: wip.grouped || {} });

      const equipData = {};
      const schemas = {};
      for (const type of types.data || []) {
        try {
          const [dataRes, schemaRes] = await Promise.all([
            api(`/equipment/list.php?type=${type}`),
            api(`/equipment/schema.php?type=${type}`)
          ]);
          equipData[type] = dataRes.data || [];
          schemas[type] = schemaRes.fields || [];
        } catch (err) {
          equipData[type] = [];
          schemas[type] = [];
        }
      }
      setEquipmentData(equipData);
      setEquipmentSchemas(schemas);
      
      if (types.data && types.data.length > 0) {
        setSelectedEquipmentType(types.data[0]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(date.getDate()).padStart(2, '0')}-${months[date.getMonth()]}-${date.getFullYear()}`;
  };

  const calculateExpiry = (calDate, months) => {
    if (!calDate) return '';
    const date = new Date(calDate);
    date.setMonth(date.getMonth() + parseInt(months));
    date.setDate(date.getDate() - 1);
    return date.toISOString().split('T')[0];
  };

  const getSerialFromRecord = (record, type) => {
    const config = equipmentConfigs[type];
    if (!config) return record.serial_number;
    return record[config.serialField] || record.serial_number;
  };

  const checkDuplicateSerial = async () => {
    const { type, serial_number, printer_sn } = newEquipmentStep1;
    const serial = type === 'PRT' ? printer_sn : serial_number;
    
    if (!serial) {
      alert('Please enter a serial number');
      return;
    }

    setLoading(true);
    try {
      const result = await api(
        `/equipment/check-duplicate.php?type=${type}&serial_number=${serial_number || ''}&printer_sn=${printer_sn || ''}`
      );

      if (result.exists) {
        setDuplicateCheck(result);
        setShowBookInModal(true);
      } else {
        // Load schema with 'new_instrument' context to exclude cal fields
        const schemaRes = await api(`/equipment/schema-enhanced.php?type=${type}&context=new_instrument`);
        
        setNewEquipment({
          type,
          serial_number: serial_number || '',
          printer_sn: printer_sn || '',
          recal: 'N',
          model: '',
          customer_name: '',
          end_user_name: '',
          sop: '',
          date_received: new Date().toISOString().split('T')[0]
        });
        
        // Store filtered schema for new instrument form
        setEquipmentSchemas(prev => ({
          ...prev,
          [`${type}_new`]: schemaRes.fields || []
        }));
        
        setShowAddEquipment(false);
        setShowAddEquipmentStep2(true);
      }
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const bookInEquipment = async () => {
    if (!bookInData.customer_name) {
      alert('Please select a customer');
      return;
    }

    setLoading(true);
    try {
      let customerId = customers.find(c => c.name === bookInData.customer_name)?.id;
      if (!customerId) {
        const custResult = await api('/customers/create.php', 'POST', { name: bookInData.customer_name });
        customerId = custResult.data.id;
        await loadData();
      }

      await api('/equipment/book-in.php', 'POST', {
        existing_id: duplicateCheck.latest_record.id,
        date_received: bookInData.date_received || null,
        recal: bookInData.recal,
        customer_id: customerId,
        sop: bookInData.sop,
        comments: bookInData.comments
      });

      setShowBookInModal(false);
      setDuplicateCheck(null);
      setBookInData({ date_received: '', recal: 'Y', customer_name: '', sop: '', comments: '' });
      await loadData();
      alert('Equipment booked in successfully!');
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createNewEquipment = async () => {
    setLoading(true);
    try {
      await api('/equipment/create-enhanced.php', 'POST', newEquipment);
      setShowAddEquipmentStep2(false);
      setNewEquipment({});
      await loadData();
      alert('Equipment created successfully!');
    } catch (err) {
      if (err.message.includes('duplicate')) {
        alert('This serial number already exists. Please use the booking-in process.');
      } else {
        alert(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const addCertificate = async () => {
    setLoading(true);
    try {
      const expDate = calculateExpiry(newCertificate.cal_date, newCertificate.exp_months);
      await api('/certificates/create.php', 'POST', { 
        ...newCertificate, 
        exp_date: expDate,
        cal_date: newCertificate.cal_date || null
      });
      setShowAddCertificate(false);
      setNewCertificate({ tool: '', serial_number: '', cal_date: '', exp_months: '12', result: 'PASS' });
      await loadData();
    } catch (err) { alert(err.message); } finally { setLoading(false); }
  };

  const openEditEquipment = (record, type) => {
    setEditingRecord({ ...record, type_code: type });
    setShowEditEquipment(true);
  };

  const updateEquipmentField = async (field, value) => {
    if (!editingRecord) return;
    try {
      await api('/equipment/update-enhanced.php', 'POST', {
        id: editingRecord.id,
        type: editingRecord.type_code,
        field,
        value
      });
      setEditingRecord({ ...editingRecord, [field]: value });
      await loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const viewEquipmentHistory = async (serial, type) => {
    setLoading(true);
    try {
      const result = await api(`/equipment/history.php?serial_number=${serial}&type=${type}`);
      setHistoryData(result);
      setShowHistoryModal(true);
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteEquipmentRecord = async (id, type) => {
    if (!confirm('Are you sure you want to delete this record?')) return;
    setLoading(true);
    try {
      await api('/equipment/delete.php', 'POST', { id, type });
      await loadData();
    } catch (err) { alert(err.message); } finally { setLoading(false); }
  };

  const exportCSV = (data, filename) => {
    if (!data.length) return;
    const headers = Object.keys(data[0]).filter(k => !['id', 'sort_date', 'custom_fields', 'created_by', 'created_at', 'updated_at'].includes(k));
    const csv = [headers.join(','), ...data.map(r => headers.map(h => `"${(r[h] || '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
  };

  const getFieldLabel = (field) => {
    return field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getFieldValue = (record, field) => {
    if (record[field] !== undefined && record[field] !== null) {
      return record[field];
    }
    if (record.custom_fields) {
      try {
        const custom = typeof record.custom_fields === 'string' 
          ? JSON.parse(record.custom_fields) 
          : record.custom_fields;
        return custom[field];
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg w-96">
          <div className="flex justify-center mb-6"><Shield className="text-blue-600" size={48} /></div>
          <h2 className="text-2xl font-bold text-center mb-6">Calibration Tracker</h2>
          <input type="text" placeholder="Username" value={loginForm.username} onChange={e => setLoginForm({ ...loginForm, username: e.target.value })} className="w-full px-4 py-2 border rounded-lg mb-4" />
          <input type="password" placeholder="Password" value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })} className="w-full px-4 py-2 border rounded-lg mb-4" onKeyPress={e => e.key === 'Enter' && handleLogin()} />
          <button onClick={handleLogin} disabled={loading} className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-blue-600 text-white p-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Calibration Certificate Tracker</h1>
            <p className="text-blue-100">Multi-equipment asset management & traceability</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-medium">{user.username}</p>
              <p className="text-sm">{user.role === 'admin' ? 'Administrator' : 'User'}</p>
            </div>
            <button onClick={handleLogout} className="px-4 py-2 bg-blue-700 rounded-lg hover:bg-blue-800">Logout</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Toolbar */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 relative min-w-64">
              <Search className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Filter..."
                value={tableFilter}
                onChange={e => setTableFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>

            <div className="flex gap-2">
              <button onClick={() => setViewMode('wip')} className={`flex items-center gap-2 px-4 py-2 rounded-lg ${viewMode === 'wip' ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}>
                <Clock size={20} /> Work In Progress
              </button>
              <button onClick={() => setViewMode('equipment')} className={`flex items-center gap-2 px-4 py-2 rounded-lg ${viewMode === 'equipment' ? 'bg-indigo-600 text-white' : 'bg-gray-200'}`}>
                <FileText size={20} /> Equipment
              </button>
            </div>

            <button onClick={() => setShowAddCertificate(true)} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              <Plus size={20} /> Certificate
            </button>

            <button onClick={() => setShowAddEquipment(true)} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700">
              <Plus size={20} /> Equipment
            </button>
          </div>
        </div>

        {loading && <div className="text-center py-16"><div className="inline-block animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600"></div></div>}

        {/* Work In Progress View */}
        {!loading && viewMode === 'wip' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">Work In Progress</h2>
                <div className="text-sm text-gray-600">
                  {wipData.data.length} items in facility
                </div>
              </div>
              
              {/* Kanban Board */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                {Object.entries(wipData.grouped).map(([statusCode, group]) => (
                  <div key={statusCode} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-sm">{group.status_label}</h3>
                      <span className="text-xs bg-gray-200 px-2 py-1 rounded-full">{group.items.length}</span>
                    </div>
                    <div className="space-y-2">
                      {group.items.map(item => (
                        <div key={item.id} className="bg-white p-3 rounded border hover:shadow cursor-pointer" onClick={() => openEditEquipment(item, item.type_code)}>
                          <div className="font-medium text-sm truncate">{getSerialFromRecord(item, item.type_code)}</div>
                          <div className="text-xs text-gray-600 truncate">{item.customer_name || item.customer}</div>
                          <div className="text-xs text-gray-500 mt-1">{item.time_status}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Equipment Type View */}
        {!loading && viewMode === 'equipment' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex gap-2 overflow-x-auto pb-2">
                {equipmentTypes.map(type => (
                  <button
                    key={type}
                    onClick={() => setSelectedEquipmentType(type)}
                    className={`px-4 py-2 rounded-lg whitespace-nowrap flex-shrink-0 ${
                      selectedEquipmentType === type 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    {equipmentConfigs[type]?.name || type} ({(equipmentData[type] || []).length})
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-6 border-b flex justify-between items-center">
                <h2 className="text-2xl font-bold">{equipmentConfigs[selectedEquipmentType]?.name || selectedEquipmentType}</h2>
                <button 
                  onClick={() => exportCSV(equipmentData[selectedEquipmentType] || [], `${selectedEquipmentType}-records.csv`)} 
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800"
                >
                  <Download size={20} /> Export CSV
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Serial</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Model</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Date Received</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Cal Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(equipmentData[selectedEquipmentType] || [])
                      .filter(row => !tableFilter || Object.values(row).some(v => 
                        v?.toString().toLowerCase().includes(tableFilter.toLowerCase())
                      ))
                      .map((row, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium">
                            <button 
                              onClick={() => viewEquipmentHistory(getSerialFromRecord(row, selectedEquipmentType), selectedEquipmentType)}
                              className="text-blue-600 hover:underline"
                            >
                              {getSerialFromRecord(row, selectedEquipmentType)}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm">{row.customer || '-'}</td>
                          <td className="px-4 py-3 text-sm">{row.model || '-'}</td>
                          <td className="px-4 py-3 text-sm">{formatDate(row.date_received)}</td>
                          <td className="px-4 py-3 text-sm">{formatDate(row.cal_date)}</td>
                          <td className="px-4 py-3 text-sm">
                            {row.status_code && (
                              <span className={`px-2 py-1 rounded text-xs font-bold ${statusColors[row.status_code] || 'bg-gray-100 text-gray-700'}`}>
                                {row.status_label || row.status_code}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="flex gap-2">
                              <button onClick={() => openEditEquipment(row, selectedEquipmentType)} className="text-blue-600 hover:text-blue-800">
                                <Edit2 size={16} />
                              </button>
                              {user.role === 'admin' && (
                                <button onClick={() => deleteEquipmentRecord(row.id, selectedEquipmentType)} className="text-red-600 hover:text-red-800">
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* STEP 1: Add Equipment - Serial Entry */}
        {showAddEquipment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowAddEquipment(false)}>
            <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold">Add Equipment - Step 1</h3>
                <button onClick={() => setShowAddEquipment(false)}><X size={28} /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Equipment Type</label>
                  <select 
                    value={newEquipmentStep1.type} 
                    onChange={e => setNewEquipmentStep1({ ...newEquipmentStep1, type: e.target.value })} 
                    className="w-full px-4 py-3 border rounded-lg"
                  >
                    {equipmentTypes.map(type => (
                      <option key={type} value={type}>{equipmentConfigs[type]?.name || type}</option>
                    ))}
                  </select>
                </div>
                {newEquipmentStep1.type === 'PRT' ? (
                  <div>
                    <label className="block text-sm font-medium mb-1">Printer Serial Number</label>
                    <input 
                      type="text" 
                      placeholder="e.g., PRT-0117" 
                      value={newEquipmentStep1.printer_sn} 
                      onChange={e => setNewEquipmentStep1({ ...newEquipmentStep1, printer_sn: e.target.value })} 
                      className="w-full px-4 py-3 border rounded-lg" 
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium mb-1">Serial Number</label>
                    <input 
                      type="text" 
                      placeholder="e.g., L-2567" 
                      value={newEquipmentStep1.serial_number} 
                      onChange={e => setNewEquipmentStep1({ ...newEquipmentStep1, serial_number: e.target.value })} 
                      className="w-full px-4 py-3 border rounded-lg" 
                    />
                  </div>
                )}
                <div className="bg-blue-50 p-4 rounded-lg text-sm">
                  <p className="font-medium mb-2">What happens next?</p>
                  <p className="text-gray-600">We'll check if this serial number exists. If it does, you'll book it in for recalibration/repair. If not, you'll fill in the full details for a new instrument.</p>
                </div>
              </div>
              <div className="flex gap-4 mt-8">
                <button onClick={checkDuplicateSerial} disabled={loading} className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400">
                  {loading ? 'Checking...' : 'Next'}
                </button>
                <button onClick={() => setShowAddEquipment(false)} className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* BOOK IN Modal */}
        {showBookInModal && duplicateCheck && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowBookInModal(false)}>
            <div className="bg-white rounded-lg p-8 w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold flex items-center gap-2">
                    <AlertTriangle className="text-orange-500" size={28} />
                    Equipment Already Exists
                  </h3>
                  <p className="text-gray-600 mt-1">Book this equipment in for recalibration/repair/inspection</p>
                </div>
                <button onClick={() => setShowBookInModal(false)}><X size={28} /></button>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="font-bold mb-2">Latest Record:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-600">Serial:</span> <span className="font-medium">{duplicateCheck.latest_record.serial_number || duplicateCheck.latest_record.printer_sn}</span></div>
                  <div><span className="text-gray-600">Customer:</span> <span className="font-medium">{duplicateCheck.latest_record.customer}</span></div>
                  <div><span className="text-gray-600">Model:</span> <span className="font-medium">{duplicateCheck.latest_record.model}</span></div>
                  <div><span className="text-gray-600">Last Cal:</span> <span className="font-medium">{formatDate(duplicateCheck.latest_record.cal_date)}</span></div>
                </div>
              </div>

              {duplicateCheck.history && duplicateCheck.history.length > 1 && (
                <div className="mb-6">
                  <h4 className="font-bold mb-2">History ({duplicateCheck.history.length} records):</h4>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {duplicateCheck.history.map((h, i) => (
                      <div key={i} className="text-sm px-3 py-2 bg-gray-50 rounded">
                        {formatDate(h.cal_date || h.date_received)} - {h.customer} - {h.recal === 'N' ? 'New' : h.recal === 'Y' ? 'Recal' : h.recal === 'R' ? 'Repair' : 'Inspection'}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <h4 className="font-bold">Book In Details:</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Date Received</label>
                    <input 
                      type="date" 
                      value={bookInData.date_received} 
                      onChange={e => setBookInData({ ...bookInData, date_received: e.target.value })} 
                      className="w-full px-4 py-2 border rounded-lg" 
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty for pre-booking</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Work Type *</label>
                    <select 
                      value={bookInData.recal} 
                      onChange={e => setBookInData({ ...bookInData, recal: e.target.value })} 
                      className="w-full px-4 py-2 border rounded-lg"
                    >
                      <option value="Y">Y - Recalibration</option>
                      <option value="R">R - Repair</option>
                      <option value="I">I - Inspection</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Customer *</label>
                  <input 
                    type="text" 
                    list="customers-list"
                    placeholder="Select or type customer name" 
                    value={bookInData.customer_name || duplicateCheck.latest_record.customer} 
                    onChange={e => setBookInData({ ...bookInData, customer_name: e.target.value })} 
                    className="w-full px-4 py-2 border rounded-lg" 
                  />
                  <datalist id="customers-list">
                    {customers.map(c => <option key={c.id} value={c.name} />)}
                  </datalist>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">SOP</label>
                    <input 
                      type="text" 
                      placeholder="5-digit SOP (optional)" 
                      value={bookInData.sop} 
                      onChange={e => setBookInData({ ...bookInData, sop: e.target.value })} 
                      className="w-full px-4 py-2 border rounded-lg" 
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Comments</label>
                  <textarea 
                    value={bookInData.comments} 
                    onChange={e => setBookInData({ ...bookInData, comments: e.target.value })} 
                    rows={3}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="Any special notes..."
                  />
                </div>
              </div>

              <div className="flex gap-4 mt-8">
                <button onClick={bookInEquipment} disabled={loading} className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center gap-2">
                  {loading ? 'Booking In...' : <><CheckCircle size={20} /> Book In Equipment</>}
                </button>
                <button onClick={() => setShowBookInModal(false)} className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: New Equipment Form (Filtered) */}
        {showAddEquipmentStep2 && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowAddEquipmentStep2(false)}>
            <div className="bg-white rounded-lg p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold">Add New Equipment</h3>
                  <p className="text-gray-600 text-sm">Calibration details will be added later</p>
                </div>
                <button onClick={() => setShowAddEquipmentStep2(false)}><X size={28} /></button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {(equipmentSchemas[`${newEquipment.type}_new`] || [])
                  .sort((a, b) => a.display_order - b.display_order)
                  .map(field => {
                    const value = newEquipment[field.field_name] || '';
                    return (
                      <div key={field.field_name} className={field.field_type === 'textarea' ? 'col-span-2' : ''}>
                        <label className="block text-sm font-medium mb-1">
                          {field.field_label} {field.is_required ? '*' : ''}
                        </label>
                        {field.field_type === 'date' ? (
                          <input
                            type="date"
                            value={value}
                            onChange={e => setNewEquipment({ ...newEquipment, [field.field_name]: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        ) : field.field_type === 'dropdown' ? (
                          field.field_name === 'customer' || field.field_name === 'end_user' ? (
                            <>
                              <input
                                type="text"
                                list={`${field.field_name}-new-list`}
                                value={newEquipment[`${field.field_name}_name`] || ''}
                                onChange={e => setNewEquipment({ ...newEquipment, [`${field.field_name}_name`]: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg"
                                placeholder={field.placeholder || 'Select or type...'}
                              />
                              <datalist id={`${field.field_name}-new-list`}>
                                {customers.map(c => <option key={c.id} value={c.name} />)}
                              </datalist>
                            </>
                          ) : (
                            <select
                              value={value}
                              onChange={e => setNewEquipment({ ...newEquipment, [field.field_name]: e.target.value })}
                              className="w-full px-3 py-2 border rounded-lg"
                            >
                              <option value="">Select...</option>
                              {(field.field_options || '').split(',').map(opt => {
                                const [val, label] = opt.includes(':') ? opt.split(':') : [opt, opt];
                                return <option key={val} value={val}>{label}</option>;
                              })}
                            </select>
                          )
                        ) : field.field_type === 'textarea' ? (
                          <textarea
                            value={value}
                            onChange={e => setNewEquipment({ ...newEquipment, [field.field_name]: e.target.value })}
                            rows={3}
                            className="w-full px-3 py-2 border rounded-lg"
                            placeholder={field.placeholder}
                          />
                        ) : (
                          <input
                            type="text"
                            value={value}
                            onChange={e => setNewEquipment({ ...newEquipment, [field.field_name]: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                            placeholder={field.placeholder}
                            disabled={['serial_number', 'printer_sn'].includes(field.field_name)}
                          />
                        )}
                        {field.help_text && <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>}
                      </div>
                    );
                  })}
              </div>
              <div className="flex gap-4 mt-8">
                <button onClick={createNewEquipment} disabled={loading} className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400">
                  {loading ? 'Creating...' : 'Create Equipment'}
                </button>
                <button onClick={() => setShowAddEquipmentStep2(false)} className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Equipment History Modal */}
        {showHistoryModal && historyData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowHistoryModal(false)}>
            <div className="bg-white rounded-lg p-8 w-full max-w-5xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold">Equipment History</h3>
                <button onClick={() => setShowHistoryModal(false)}><X size={28} /></button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h4 className="font-bold mb-3">Calibration History ({historyData.total_bookings} records)</h4>
                  <div className="space-y-3">
                    {historyData.calibration_history.map((record, idx) => (
                      <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className={`px-3 py-1 rounded text-sm font-bold ${statusColors[record.status_code] || 'bg-gray-100'}`}>
                              {record.status_label || 'Unknown'}
                            </span>
                            <span className="ml-2 text-sm text-gray-600">
                              {record.recal === 'N' ? 'New Instrument' : 
                               record.recal === 'Y' ? 'Recalibration' : 
                               record.recal === 'R' ? 'Repair' : 
                               'Inspection'}
                            </span>
                          </div>
                          <div className="text-right text-sm">
                            <p className="font-medium">{formatDate(record.created_at)}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-sm mt-3">
                          <div><span className="text-gray-600">Customer:</span> <span className="font-medium">{record.customer_name}</span></div>
                          <div><span className="text-gray-600">Received:</span> <span className="font-medium">{formatDate(record.date_received)}</span></div>
                          <div><span className="text-gray-600">Dispatched:</span> <span className="font-medium">{formatDate(record.date_dispatched)}</span></div>
                          <div><span className="text-gray-600">Cal Date:</span> <span className="font-medium">{formatDate(record.cal_date)}</span></div>
                          <div><span className="text-gray-600">Exp Date:</span> <span className="font-medium">{formatDate(record.exp_date)}</span></div>
                          <div><span className="text-gray-600">Result:</span> 
                            {record.result && (
                              <span className={`ml-1 px-2 py-0.5 rounded text-xs font-bold ${
                                record.result === 'PASS' ? 'bg-green-100 text-green-800' : 
                                record.result === 'FAIL' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {record.result}
                              </span>
                            )}
                          </div>
                        </div>
                        {record.comments && (
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">Comments:</span> {record.comments}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {historyData.ownership_history && historyData.ownership_history.length > 0 && (
                  <div>
                    <h4 className="font-bold mb-3">Ownership History</h4>
                    <div className="space-y-2">
                      {historyData.ownership_history.map((owner, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-blue-50 rounded">
                          <div>
                            <span className="font-medium">{owner.customer_name}</span>
                            <span className={`ml-2 text-xs px-2 py-1 rounded ${owner.ownership_status === 'Current' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                              {owner.ownership_status}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {formatDate(owner.start_date)} {owner.end_date && `- ${formatDate(owner.end_date)}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button onClick={() => setShowHistoryModal(false)} className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Certificate Modal (unchanged) */}
        {showAddCertificate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowAddCertificate(false)}>
            <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold">Add New Certificate</h3>
                <button onClick={() => setShowAddCertificate(false)}><X size={28} /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Tool (Acronym)</label>
                  <input type="text" placeholder="e.g., LFM, GFM, DTM" value={newCertificate.tool} onChange={e => setNewCertificate({ ...newCertificate, tool: e.target.value.toUpperCase() })} className="w-full px-4 py-3 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Serial Number</label>
                  <input type="text" placeholder="Serial Number" value={newCertificate.serial_number} onChange={e => setNewCertificate({ ...newCertificate, serial_number: e.target.value })} className="w-full px-4 py-3 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Calibration Date</label>
                  <input type="date" value={newCertificate.cal_date} onChange={e => setNewCertificate({ ...newCertificate, cal_date: e.target.value })} className="w-full px-4 py-3 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Validity Period</label>
                  <select value={newCertificate.exp_months} onChange={e => setNewCertificate({ ...newCertificate, exp_months: e.target.value })} className="w-full px-4 py-3 border rounded-lg">
                    <option value="3">3 months</option>
                    <option value="6">6 months</option>
                    <option value="12">12 months</option>
                    <option value="24">24 months</option>
                  </select>
                </div>
                {newCertificate.cal_date && (
                  <div className="bg-blue-50 p-3 rounded-lg text-sm">
                    <p className="font-medium">Expiry Date:</p>
                    <p className="text-blue-800">{formatDate(calculateExpiry(newCertificate.cal_date, newCertificate.exp_months))}</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-1">Result</label>
                  <select value={newCertificate.result} onChange={e => setNewCertificate({ ...newCertificate, result: e.target.value })} className="w-full px-4 py-3 border rounded-lg">
                    <option>PASS</option>
                    <option>FAIL</option>
                    <option>LIMITED</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-4 mt-8">
                <button onClick={addCertificate} disabled={loading} className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400">
                  {loading ? 'Creating...' : 'Create Certificate'}
                </button>
                <button onClick={() => setShowAddCertificate(false)} className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Equipment Modal (with status dropdown) */}
        {showEditEquipment && editingRecord && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowEditEquipment(false)}>
            <div className="bg-white rounded-lg p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold">Edit {equipmentConfigs[editingRecord.type_code]?.name || editingRecord.type_code} Record</h3>
                <button onClick={() => setShowEditEquipment(false)}><X size={28} /></button>
              </div>
              
              {/* Status selector */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium mb-2">Status</label>
                <select
                  value={editingRecord.status_id || ''}
                  onChange={e => updateEquipmentField('status_id', e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="">Select status...</option>
                  {statuses.map(status => (
                    <option key={status.id} value={status.id}>{status.status_label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {(equipmentSchemas[editingRecord.type_code] || [])
                  .sort((a, b) => a.display_order - b.display_order)
                  .map(field => {
                    const value = getFieldValue(editingRecord, field.field_name);
                    return (
                      <div key={field.field_name} className={field.field_type === 'textarea' ? 'col-span-2' : ''}>
                        <label className="block text-sm font-medium mb-1">{field.field_label}</label>
                        {field.field_type === 'date' ? (
                          <input
                            type="date"
                            value={value || ''}
                            onChange={e => updateEquipmentField(field.field_name, e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        ) : field.field_type === 'dropdown' ? (
                          <select
                            value={value || ''}
                            onChange={e => updateEquipmentField(field.field_name, e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg"
                            disabled={field.field_name === 'model'}
                          >
                            <option value="">Select...</option>
                            {(field.field_options || '').split(',').map(opt => {
                              const [val, label] = opt.includes(':') ? opt.split(':') : [opt, opt];
                              return <option key={val} value={val}>{label}</option>;
                            })}
                          </select>
                        ) : field.field_type === 'textarea' ? (
                          <textarea
                            value={value || ''}
                            onChange={e => updateEquipmentField(field.field_name, e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 border rounded-lg"
                            placeholder={field.placeholder}
                          />
                        ) : field.field_type === 'decimal' ? (
                          <input
                            type="number"
                            step="0.01"
                            value={value || ''}
                            onChange={e => updateEquipmentField(field.field_name, e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg"
                            placeholder={field.placeholder}
                          />
                        ) : (
                          <input
                            type="text"
                            value={value || ''}
                            onChange={e => updateEquipmentField(field.field_name, e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg"
                            placeholder={field.placeholder}
                            disabled={field.field_name === 'model'}
                          />
                        )}
                        {field.help_text && <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>}
                        {field.field_name === 'model' && <p className="text-xs text-blue-600 mt-1">Model type is locked (remembered from first entry)</p>}
                      </div>
                    );
                  })}
              </div>
              <div className="flex gap-4 mt-8">
                <button onClick={() => setShowEditEquipment(false)} className="flex-1 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  Done
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalibrationTracker;